import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  EnvelopeIcon,
  PhoneIcon,
  LockClosedIcon,
  UserCircleIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import authService from '../../services/auth.service';
import { User, UpdateAccountRequest, ProductLine } from '../../types/auth.types';
import AuthenticatedLayout from '../AuthenticatedLayout';

// Product line display configuration
const PRODUCT_CONFIG: Record<ProductLine, {
  name: string;
  tagline: string;
  badge: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
}> = {
  comply: {
    name: 'CallCov COMPLY',
    tagline: 'Compliance monitoring that never sleeps',
    badge: 'bg-blue-100 text-blue-800',
    icon: ShieldCheckIcon,
  },
  quality: {
    name: 'CallCov QUALITY',
    tagline: '100% call visibility, zero manual QA',
    badge: 'bg-green-100 text-green-800',
    icon: ChartBarIcon,
  },
  full: {
    name: 'CallCov FULL',
    tagline: 'Complete call intelligence platform',
    badge: 'bg-purple-100 text-purple-800',
    icon: SparklesIcon,
  },
};

const AccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UpdateAccountRequest>({
    email: '',
    phoneNumber: '',
    password: '',
    currentPassword: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isEditing, setIsEditing] = useState<Record<string, boolean>>({
    email: false,
    phoneNumber: false,
    password: false,
  });
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setFormData((prev) => ({
        ...prev,
        email: userData.email,
        phoneNumber: userData.phone_number,
      }));
    } catch (error) {
      navigate('/signin');
    } finally {
      setIsLoadingUser(false);
    }
  };

  const validateField = (field: string, value: string): string | null => {
    switch (field) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return t('accountSettings:validation.emailInvalid');
        }
        break;
      case 'phoneNumber':
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ''))) {
          return t('accountSettings:validation.phoneInvalid');
        }
        break;
      case 'password':
        if (value && value.length < 8) {
          return t('accountSettings:validation.passwordMinLength');
        }
        if (value && !/[A-Z]/.test(value)) {
          return t('accountSettings:validation.passwordUppercase');
        }
        if (value && !/[a-z]/.test(value)) {
          return t('accountSettings:validation.passwordLowercase');
        }
        if (value && !/\d/.test(value)) {
          return t('accountSettings:validation.passwordDigit');
        }
        if (value && !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
          return t('accountSettings:validation.passwordSpecialChar');
        }
        if (value && value !== confirmPassword) {
          return t('accountSettings:validation.passwordsDoNotMatch');
        }
        break;
    }
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'confirmPassword') {
      setConfirmPassword(value);
      if (formData.password && value !== formData.password) {
        setErrors((prev) => ({ ...prev, confirmPassword: t('accountSettings:validation.passwordsDoNotMatch') }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          delete newErrors.password;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      const error = validateField(name, value);
      if (error) {
        setErrors((prev) => ({ ...prev, [name]: error }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  const handleEdit = (field: string) => {
    setIsEditing((prev) => ({ ...prev, [field]: true }));
    setSuccessMessage('');
    setErrors({});
  };

  const handleCancel = (field: string) => {
    setIsEditing((prev) => ({ ...prev, [field]: false }));

    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email,
        phoneNumber: user.phone_number,
        password: '',
        currentPassword: '',
      }));
      setConfirmPassword('');
    }

    setErrors({});
  };

  const handleSave = async (field: string) => {
    const updateData: UpdateAccountRequest = {
      currentPassword: formData.currentPassword || ''
    };

    if (field === 'email') {
      const error = validateField('email', formData.email || '');
      if (error) {
        setErrors({ email: error });
        return;
      }
      updateData.email = formData.email;
    } else if (field === 'phoneNumber') {
      const error = validateField('phoneNumber', formData.phoneNumber || '');
      if (error) {
        setErrors({ phoneNumber: error });
        return;
      }
      updateData.phoneNumber = formData.phoneNumber;
    } else if (field === 'password') {
      if (!formData.password) {
        setErrors({ password: t('accountSettings:validation.newPasswordRequired') });
        return;
      }
      const error = validateField('password', formData.password);
      if (error) {
        setErrors({ password: error });
        return;
      }
      if (formData.password !== confirmPassword) {
        setErrors({ confirmPassword: t('accountSettings:validation.passwordsDoNotMatch') });
        return;
      }
      updateData.password = formData.password;
    }

    setIsLoading(true);
    try {
      const updatedUser = await authService.updateAccount(updateData);
      setUser(updatedUser);
      setIsEditing((prev) => ({ ...prev, [field]: false }));
      setFormData((prev) => ({ ...prev, currentPassword: '', password: '' }));
      setConfirmPassword('');

      let successKey = '';
      if (field === 'email') {
        successKey = 'messages.emailUpdated';
      } else if (field === 'phoneNumber') {
        successKey = 'messages.phoneNumberUpdated';
      } else if (field === 'password') {
        successKey = 'messages.passwordUpdated';
      }

      setSuccessMessage(t(`accountSettings:${successKey}`));
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      if (error.response?.data?.detail) {
        setErrors({ [field]: error.response.data.detail });
      } else if (error.response?.data?.message) {
        setErrors({ [field]: error.response.data.message });
      } else {
        setErrors({ general: t('accountSettings:messages.updateFailed') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    authService.signOut();
  };

  if (isLoadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <AuthenticatedLayout>
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            {t('accountSettings:header.backToDashboard')}
          </button>
        </div>
        <div className="mt-6">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">{t('accountSettings:header.title')}</h1>
        </div>
      </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-10">
            {successMessage && (
              <div className="mb-6 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {errors.general && (
              <div className="mb-6 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{errors.general}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">{t('accountSettings:profileInfo.title')}</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  {t('accountSettings:profileInfo.description')}
                </p>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  {/* Email Section */}
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 flex items-center">
                      <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-400" />
                      {t('accountSettings:fields.emailAddress')}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {isEditing.email ? (
                        <div>
                          <div className="flex gap-2">
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className={`flex-1 rounded-md border ${
                                errors.email ? 'border-red-300' : 'border-gray-300'
                              } px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                            />
                            <button
                              onClick={() => handleSave('email')}
                              disabled={isLoading}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                              {isLoading ? t('accountSettings:actions.saving') : t('accountSettings:actions.save')}
                            </button>
                            <button
                              onClick={() => handleCancel('email')}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              {t('accountSettings:actions.cancel')}
                            </button>
                          </div>
                          {errors.email && (
                            <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span>{user.email}</span>
                          <button
                            onClick={() => handleEdit('email')}
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            {t('accountSettings:actions.edit')}
                          </button>
                        </div>
                      )}
                    </dd>
                  </div>

                  {/* Phone Number Section */}
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 flex items-center">
                      <PhoneIcon className="h-5 w-5 mr-2 text-gray-400" />
                      {t('accountSettings:fields.phoneNumber')}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {isEditing.phoneNumber ? (
                        <div>
                          <div className="flex gap-2">
                            <input
                              type="tel"
                              name="phoneNumber"
                              value={formData.phoneNumber}
                              onChange={handleInputChange}
                              className={`flex-1 rounded-md border ${
                                errors.phoneNumber ? 'border-red-300' : 'border-gray-300'
                              } px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                            />
                            <button
                              onClick={() => handleSave('phoneNumber')}
                              disabled={isLoading}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                              {isLoading ? t('accountSettings:actions.saving') : t('accountSettings:actions.save')}
                            </button>
                            <button
                              onClick={() => handleCancel('phoneNumber')}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              {t('accountSettings:actions.cancel')}
                            </button>
                          </div>
                          {errors.phoneNumber && (
                            <p className="mt-2 text-sm text-red-600">{errors.phoneNumber}</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span>{user.phone_number}</span>
                          <button
                            onClick={() => handleEdit('phoneNumber')}
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            {t('accountSettings:actions.edit')}
                          </button>
                        </div>
                      )}
                    </dd>
                  </div>

                  {/* Password Section */}
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 flex items-center">
                      <LockClosedIcon className="h-5 w-5 mr-2 text-gray-400" />
                      {t('accountSettings:fields.password')}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {isEditing.password ? (
                        <div className="space-y-3">
                          <div>
                            <input
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              placeholder={t('accountSettings:placeholders.newPassword')}
                              className={`w-full rounded-md border ${
                                errors.password ? 'border-red-300' : 'border-gray-300'
                              } px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                            />
                            {errors.password && (
                              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                            )}
                          </div>
                          <div>
                            <input
                              type="password"
                              name="confirmPassword"
                              value={confirmPassword}
                              onChange={handleInputChange}
                              placeholder={t('accountSettings:placeholders.confirmNewPassword')}
                              className={`w-full rounded-md border ${
                                errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                              } px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                            />
                            {errors.confirmPassword && (
                              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSave('password')}
                              disabled={isLoading}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                              {isLoading ? t('accountSettings:actions.saving') : t('accountSettings:actions.updatePassword')}
                            </button>
                            <button
                              onClick={() => handleCancel('password')}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              {t('accountSettings:actions.cancel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span>••••••••</span>
                          <button
                            onClick={() => handleEdit('password')}
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            {t('accountSettings:actions.change')}
                          </button>
                        </div>
                      )}
                    </dd>
                  </div>

                  {/* Account Status */}
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">{t('accountSettings:fields.accountStatus')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div className="flex items-center space-x-2">
                        {user.is_verified ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="mr-1.5 h-4 w-4" />
                            {t('accountSettings:status.verified')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <ExclamationCircleIcon className="mr-1.5 h-4 w-4" />
                            {t('accountSettings:status.unverified')}
                          </span>
                        )}
                        {user.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t('accountSettings:status.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {t('accountSettings:status.inactive')}
                          </span>
                        )}
                      </div>
                    </dd>
                  </div>

                  {/* Product Line */}
                  {user.product_line && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">{t('accountSettings:fields.product', 'Product')}</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {(() => {
                              const config = PRODUCT_CONFIG[user.product_line as ProductLine];
                              const Icon = config?.icon;
                              return (
                                <>
                                  {Icon && <Icon className="h-6 w-6 text-gray-400" />}
                                  <div>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.badge || 'bg-gray-100 text-gray-800'}`}>
                                      {config?.name || user.product_line.toUpperCase()}
                                    </span>
                                    <p className="mt-1 text-xs text-gray-500">{config?.tagline}</p>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                          <Link
                            to="/billing"
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            {user.product_line !== 'full'
                              ? t('accountSettings:actions.upgrade', 'Upgrade')
                              : t('accountSettings:actions.managePlan', 'Manage Plan')
                            }
                          </Link>
                        </div>
                      </dd>
                    </div>
                  )}

                  {/* Member Since */}
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">{t('accountSettings:fields.memberSince')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {new Date(user.created_at).toLocaleDateString(i18n.language || 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSignOut}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {t('accountSettings:actions.signOut')}
              </button>
            </div>
          </div>
        </main>
    </AuthenticatedLayout>
  );
};

export default AccountSettings;